import React, { useEffect, useState, useRef } from 'react';
import { AlertCircle, Check, Lock } from 'lucide-react';
interface PinEntryProps {
  correctPin: string;
  onPinVerified: () => void;
}
export const PinEntry: React.FC<PinEntryProps> = ({
  correctPin,
  onPinVerified
}) => {
  const [pin, setPin] = useState<string[]>(Array(correctPin.length).fill(''));
  const [error, setError] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Initialize the refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, correctPin.length);
  }, [correctPin.length]);
  const handlePinChange = (index: number, value: string) => {
    // Reset error state when user starts typing again
    if (error) setError(false);
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return;
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    // Auto-focus next input if a digit was entered
    if (value && index < correctPin.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const verifyPin = () => {
    const enteredPin = pin.join('');
    if (enteredPin === correctPin) {
      setSuccess(true);
      setTimeout(() => {
        onPinVerified();
      }, 1000);
    } else {
      setError(true);
      // Reset PIN on error
      setPin(Array(correctPin.length).fill(''));
      // Focus first input
      inputRefs.current[0]?.focus();
    }
  };
  return <div className="w-full min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center" data-id="element-746">
      <div className="w-full max-w-md p-8 bg-gray-800 rounded-xl border border-gray-700 shadow-lg" data-id="element-747">
        <div className="flex flex-col items-center mb-8" data-id="element-748">
          <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mb-4" data-id="element-749">
            <Lock size={32} className="text-blue-400" data-id="element-750" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2" data-id="element-751">Systemzugang</h1>
          <p className="text-gray-400 text-center" data-id="element-752">
            Bitte geben Sie den PIN-Code ein, um auf das Fass Braun
            Produktionssystem zuzugreifen
          </p>
        </div>
        {error && <div className="mb-6 p-3 bg-red-900/30 border border-red-800/50 rounded-lg flex items-center gap-2" data-id="element-753">
            <AlertCircle size={18} className="text-red-400" data-id="element-754" />
            <span className="text-red-300" data-id="element-755">
              Falscher PIN-Code. Bitte versuchen Sie es erneut.
            </span>
          </div>}
        {success && <div className="mb-6 p-3 bg-green-900/30 border border-green-800/50 rounded-lg flex items-center gap-2" data-id="element-756">
            <Check size={18} className="text-green-400" data-id="element-757" />
            <span className="text-green-300" data-id="element-758">
              PIN-Code korrekt. Sie werden weitergeleitet...
            </span>
          </div>}
        <div className="flex justify-center gap-3 mb-6" data-id="element-759">
          {Array.from({
          length: correctPin.length
        }).map((_, index) => <input key={index} ref={el => inputRefs.current[index] = el} type="text" maxLength={1} className={`w-12 h-14 text-center text-xl font-bold bg-gray-700 border ${error ? 'border-red-500' : success ? 'border-green-500' : 'border-gray-600'} rounded-lg focus:outline-none focus:ring-2 ${error ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`} value={pin[index]} onChange={e => handlePinChange(index, e.target.value)} onKeyDown={e => handleKeyDown(index, e)} disabled={success} data-id="element-760" />)}
        </div>
        <button className={`w-full py-3 rounded-lg font-medium ${pin.every(digit => digit) && !success ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-700 text-gray-400 cursor-not-allowed'}`} onClick={verifyPin} disabled={!pin.every(digit => digit) || success} data-id="element-761">
          {success ? 'Verifiziert' : 'Verifizieren'}
        </button>
        <div className="mt-6 text-center" data-id="element-762">
          <p className="text-gray-500 text-sm" data-id="element-763">
            Fass Braun GmbH • Produktionssystem
          </p>
          <p className="text-gray-500 text-sm mt-1" data-id="element-764">
            © {new Date().getFullYear()} Alle Rechte vorbehalten
          </p>
        </div>
      </div>
    </div>;
};